"use client";
/**
 * ChartQuickLook: the one quick chart window of the app. A small window above
 * the page with the chart of one symbol, its name, a link to the symbol's
 * full Stock Brief, and a close button; Escape and a press outside close it.
 * Asked for by the owner on 2026-10-14 for The Week in Earnings ("a small
 * window that is the chart when we click chart so we can have a quick
 * glimpse"). A list that wants a quick chart calls `openChartQuickLook`
 * (`lib/chart/chartQuickLook.ts`); it never draws a window of its own.
 *
 * `ChartQuickLookHost` is mounted once, in `PrefsSyncGate` beside the desks'
 * keep-alive host (inside the theme the chart needs), and closes the window
 * when the route changes.
 *
 * The chart (`ChartQuickLookChart.tsx`) is loaded at the first opening, so
 * no page carries its code until then.
 *
 * Layering. The window sits at z-[60], the band of a maximized pane, and is
 * put on the page after it, so it covers one; the chart's own menus and
 * dialogs open at 60 and above, later still, so they draw over the window.
 * No transform or filter on any element around the chart: either would make
 * the chart's fixed menus place themselves against the window and not the
 * screen. The blur is on the backdrop, a sibling.
 *
 * Size. A window, not a pane: it reads the screen (vw, dvh), as the app's
 * dialogs do. `min(56rem, 94vw)` wide and `min(56rem, 90dvh)` tall: most of
 * the screen's height. It opens on the Scanner - Standard template, whose
 * thirteen indicators each take a pane, and at 32rem tall (the first size)
 * the price chart was a thin strip (the owner, 2026-10-15: "make it taller").
 */
import { useEffect, useRef, useState, useSyncExternalStore, type PointerEvent as ReactPointerEvent } from "react";
import { createPortal } from "react-dom";
import dynamic from "@/lib/spa/dynamic";
import Link from "@/lib/spa/Link";
import { usePathname } from "@/lib/spa/navigation";
import { stockBriefHref } from "@/lib/stock/briefHref";
import {
  chartQuickLookNow,
  closeChartQuickLook,
  subscribeChartQuickLook,
} from "@/lib/chart/chartQuickLook";
import { QUICK_LOOK_SIZE_KEY, parseQuickLookSize, resizedQuickLook, type QuickLookSize } from "@/lib/chart/quickLookSize";
import { safeLocalStorageGet, safeLocalStorageRemove, safeLocalStorageSet } from "@/lib/safeLocalStorage";

const ChartQuickLookChart = dynamic(
  () => import("@/components/chart/ChartQuickLookChart").then((m) => m.ChartQuickLookChart),
  { ssr: false },
);

export function ChartQuickLook({
  symbol,
  name,
  seq,
  onClose,
}: {
  symbol: string;
  name?: string | null;
  seq?: number;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const boxRef = useRef<HTMLElement>(null);
  // The size the reader dragged it to on this machine; null is the starting size.
  const [size, setSize] = useState<QuickLookSize | null>(() => parseQuickLookSize(safeLocalStorageGet(QUICK_LOOK_SIZE_KEY)));
  const drag = useRef<{ x: number; y: number; start: QuickLookSize; now: QuickLookSize } | null>(null);
  const screen = (): QuickLookSize => ({ w: window.innerWidth, h: window.innerHeight });
  const onGripDown = (e: ReactPointerEvent<HTMLSpanElement>) => {
    const box = boxRef.current;
    if (!box || e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    const r = box.getBoundingClientRect();
    const start = { w: Math.round(r.width), h: Math.round(r.height) };
    drag.current = { x: e.clientX, y: e.clientY, start, now: start };
  };
  const onGripMove = (e: ReactPointerEvent<HTMLSpanElement>) => {
    const d = drag.current;
    if (!d) return;
    d.now = resizedQuickLook(d.start, e.clientX - d.x, e.clientY - d.y, screen());
    setSize(d.now);
  };
  const onGripUp = () => {
    const d = drag.current;
    drag.current = null;
    if (d && (d.now.w !== d.start.w || d.now.h !== d.start.h)) safeLocalStorageSet(QUICK_LOOK_SIZE_KEY, JSON.stringify(d.now));
  };
  const onGripReset = () => {
    drag.current = null;
    safeLocalStorageRemove(QUICK_LOOK_SIZE_KEY);
    setSize(null);
  };

  useEffect(() => {
    closeRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // A dialog of the chart's own that took the key keeps it.
      if (e.key !== "Escape" || e.defaultPrevented) return;
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-[60]" data-chart-quick-look="">
      <div aria-hidden className="absolute inset-0 bg-ink/45 backdrop-blur-[2px]" onMouseDown={onClose} />
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-2">
        <section
          ref={boxRef}
          role="dialog"
          aria-modal="true"
          aria-label={`Chart of ${symbol}`}
          className={`pointer-events-auto relative flex flex-col overflow-hidden rounded-xl border border-line bg-surface shadow-2xl ${size ? "" : "h-[min(56rem,90dvh)] w-[min(56rem,94vw)]"}`}
          // A dragged size, held within the screen when the screen is smaller than when it was dragged.
          style={size ? { width: `min(${size.w}px, calc(100vw - 16px))`, height: `min(${size.h}px, calc(100dvh - 16px))` } : undefined}
        >
          <header className="flex shrink-0 items-center gap-2 border-b border-line px-3 py-2">
            <div className="flex min-w-0 flex-1 items-baseline gap-2">
              <span className="shrink-0 text-sm font-bold tracking-wide text-ink">{symbol}</span>
              {name ? <span className="min-w-0 truncate text-xs text-ink-muted">{name}</span> : null}
            </div>
            <Link
              href={stockBriefHref(symbol)}
              title={`The Stock Brief of ${symbol}: chart, earnings, income, news and profile`}
              onClick={(e) => {
                if (e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) onClose();
              }}
              className="shrink-0 rounded-md px-2 py-1 text-2xs font-semibold text-accent ring-1 ring-line hover:bg-accent-soft"
            >
              Open full brief
            </Link>
            <button
              ref={closeRef}
              type="button"
              aria-label="Close"
              title="Close (Esc)"
              onClick={onClose}
              className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-ink-muted transition hover:bg-surface-muted hover:text-ink"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </header>
          <div className="min-h-0 flex-1">
            <ChartQuickLookChart symbol={symbol} seq={seq} />
          </div>
          <span
            role="separator"
            aria-label="Resize the chart window"
            title="Drag to resize; double-click for the starting size"
            onPointerDown={onGripDown}
            onPointerMove={onGripMove}
            onPointerUp={onGripUp}
            onPointerCancel={onGripUp}
            onDoubleClick={onGripReset}
            className="absolute bottom-0 right-0 z-10 grid h-4 w-4 cursor-nwse-resize touch-none place-items-end p-0.5 text-ink-faint hover:text-ink"
          >
            <svg viewBox="0 0 10 10" className="h-2.5 w-2.5" aria-hidden>
              <path d="M9 1L1 9M9 5L5 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
            </svg>
          </span>
        </section>
      </div>
    </div>,
    document.body,
  );
}

/** Draws the window `openChartQuickLook` asked for, and nothing otherwise. */
export function ChartQuickLookHost() {
  const look = useSyncExternalStore(subscribeChartQuickLook, chartQuickLookNow, () => null);
  const pathname = usePathname();
  const shownOn = useRef(pathname);
  useEffect(() => {
    if (shownOn.current === pathname) return;
    shownOn.current = pathname;
    closeChartQuickLook();
  }, [pathname]);
  if (!look) return null;
  return <ChartQuickLook symbol={look.symbol} name={look.name} seq={look.seq} onClose={closeChartQuickLook} />;
}
